import { DARK_THEME, LIGHT_THEME } from 'common/utils/constants/theme';
import { apiInstance } from 'common/utils/axios';

export const THEME_STORAGE_KEY = 'app-theme';

/** Evita que ThemeDocumentSync pise un toggle manual del menú. */
let themeManuallySet = false;

export function wasThemeManuallySet() {
  return themeManuallySet;
}

/**
 * Resuelve el theme_id del usuario (número, string, o entidad/currentUser).
 * Soporta `theme_id` (API /users/me) y `theme` ('dark'|'light' de SCI entity).
 *
 * @param {number|string|{ theme_id?: number|string, theme?: string, user?: object }|null|undefined} themeOrUser
 * @returns {number|undefined}
 */
export function resolveThemeId(themeOrUser) {
  if (themeOrUser == null) {
    return undefined;
  }

  if (typeof themeOrUser === 'object') {
    if (themeOrUser.theme_id != null && themeOrUser.theme_id !== '') {
      const id = Number(themeOrUser.theme_id);
      if (id === DARK_THEME || id === LIGHT_THEME) {
        return id;
      }
    }
    if (themeOrUser.theme != null) {
      const name = String(themeOrUser.theme).toLowerCase();
      if (name === 'dark') {
        return DARK_THEME;
      }
      if (name === 'light') {
        return LIGHT_THEME;
      }
    }
    if (themeOrUser.user) {
      return resolveThemeId(themeOrUser.user);
    }
    return undefined;
  }

  const id = Number(themeOrUser);
  if (id === DARK_THEME || id === LIGHT_THEME) {
    return id;
  }
  return undefined;
}

/**
 * True cuando ya conocemos el tema del usuario (theme_id o theme string).
 * Mientras sea false no hay que renderizar light/dark final (evitar flash SCI→suppliers).
 *
 * @param {number|string|{ theme_id?: number|string, theme?: string, user?: object }|null|undefined} themeOrUser
 * @returns {boolean}
 */
export function isThemeResolved(themeOrUser) {
  return resolveThemeId(themeOrUser) != null;
}

/**
 * Indica si el usuario usa tema dark.
 * Por defecto (sin theme_id) se considera light.
 *
 * @param {number|string|{ theme_id?: number|string, theme?: string, user?: object }|null|undefined} themeOrUser
 * @returns {boolean}
 */
export function isDarkTheme(themeOrUser) {
  return resolveThemeId(themeOrUser) === DARK_THEME;
}

/**
 * Indica si el usuario usa tema light.
 *
 * @param {number|string|{ theme_id?: number|string, theme?: string, user?: object }|null|undefined} themeOrUser
 * @returns {boolean}
 */
export function isLightTheme(themeOrUser) {
  return isThemeResolved(themeOrUser) && !isDarkTheme(themeOrUser);
}

/**
 * @param {number|string|{ theme_id?: number|string, theme?: string, user?: object }|null|undefined} themeOrUser
 * @returns {'light'|'dark'|undefined}
 */
export function getUserTheme(themeOrUser) {
  if (!isThemeResolved(themeOrUser)) {
    return undefined;
  }
  return isDarkTheme(themeOrUser) ? 'dark' : 'light';
}

/**
 * @param {'light'|'dark'|string|null|undefined} theme
 * @returns {'light'|'dark'|undefined}
 */
export function normalizeThemeName(theme) {
  if (theme === 'dark' || theme === 'light') {
    return theme;
  }
  return undefined;
}

/**
 * @param {'light'|'dark'} theme
 * @returns {number}
 */
export function themeIdFromName(theme) {
  return theme === 'dark' ? DARK_THEME : LIGHT_THEME;
}

/**
 * Tema activo en el documento (fuente de verdad para CSS).
 * @returns {'light'|'dark'|undefined}
 */
export function getDocumentTheme() {
  if (typeof document === 'undefined') {
    return undefined;
  }
  return normalizeThemeName(document.body.getAttribute('data-theme'));
}

/**
 * @returns {'light'|'dark'|undefined}
 */
export function getStoredTheme() {
  try {
    return normalizeThemeName(localStorage.getItem(THEME_STORAGE_KEY));
  } catch (e) {
    return undefined;
  }
}

/**
 * Aplica el tema vía atributo CSS. No dispara re-render de React.
 *
 * @param {'light'|'dark'} theme
 * @param {{ persist?: boolean, userId?: number|string|null, manual?: boolean }} [options]
 */
export function applyTheme(theme, { persist = false, userId = null, manual = false } = {}) {
  const next = normalizeThemeName(theme);
  if (!next) {
    return;
  }

  document.body.setAttribute('data-theme', next);
  if (document.documentElement) {
    document.documentElement.setAttribute('data-theme', next);
  }

  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch (e) {
    // ignore quota / private mode
  }

  if (manual) {
    themeManuallySet = true;
  }

  if (persist && userId != null) {
    persistThemeToApi(next, userId);
  }
}

/**
 * @param {'light'|'dark'} theme
 * @param {number|string} userId
 */
export function persistThemeToApi(theme, userId) {
  if (userId == null) {
    return;
  }
  const themeId = themeIdFromName(theme);
  apiInstance
    .put(`/users/${userId}`, { theme_id: themeId })
    .catch(error => {
      console.log(error);
    });
}
